<template>
  <nav class="main-nav">
    <template v-if="isSignedIn">
      <ul class="main-nav__list main-nav__list--left">
        <li class="main-nav__item">
          <router-link
            :to="{ name: 'Home' }"
            class="main-nav__logo main-nav__link"
          >
            <img
              :src="clientLogo"
              class="main-nav__logo"
              alt="Logo"
            >
          </router-link>
        </li>
      </ul>

      <ul class="main-nav__list main-nav__list--center">
        <li class="main-nav__item">
          <router-link
            :to="{ name: 'Stream' }"
            :class="{ 'main-nav__link--new': streamNewEntries }"
            class="main-nav__link"
          >
            <font-awesome-icon
              icon="history"
              size="lg"
            />
          </router-link>
        </li>
        <li class="main-nav__item">
          <router-link
            :to="{ name: 'Folders' }"
            :class="{ 'router-link-active': isFoldersRoute }"
            class="main-nav__link"
          >
            <font-awesome-icon
              icon="th"
              size="lg"
            />
          </router-link>
        </li>
        <li class="main-nav__item">
          <router-link
            :to="{ name: 'Teams' }"
            :class="{ 'router-link-active': isTeamsRoute }"
            class="main-nav__link"
          >
            <font-awesome-icon
              icon="users"
              size="lg"
            />
          </router-link>
        </li>
        <li class="main-nav__item">
          <router-link
            :to="{ name: 'Search' }"
            class="main-nav__link"
          >
            <font-awesome-icon
              icon="search"
              size="lg"
            />
          </router-link>
        </li>
      </ul>

      <ul class="main-nav__list main-nav__list--right">
        <li class="main-nav__item main-nav__item--user">
          <dropdown-input
            :options="userDropdownOptions"
            position="right"
            @select="handleUserDropdownSelect"
          >
            <button>
              <avatar
                :name="currentUser | fullName"
                size="md"
                class="avatar"
              />
            </button>
          </dropdown-input>
        </li>
      </ul>
    </template>

    <template v-if="!isSignedIn">
      <ul class="main-nav__list main-nav__list--left">
        <li class="main-nav__item">
          <router-link
            :to="{ name: 'Home' }"
            class="main-nav__link main-nav__logo"
          >
            <img
              :src="clientDefaultLogo"
              class="main-nav__logo"
              alt="Logo"
            >
          </router-link>
        </li>
      </ul>

      <ul class="main-nav__list main-nav__list--right">
        <li
          v-if="false && $route.name !== 'SignUpPage'"
          class="main-nav__item"
        >
          <router-link
            v-t="'c.mainNav.navigation.signup'"
            :to="{ name: 'SignUpPage' }"
            class="button button--1 jsb_ jsb_Overlay"
          />
        </li>
        <li
          v-if="$route.name !== 'SignInPage'"
          class="main-nav__item"
        >
          <router-link
            :to="{ name: 'SignInPage' }"
            class="button button--1 jsb_ jsb_Overlay"
          >
            <font-awesome-icon
              icon="sign-in-alt"
              size="lg"
            />
            <span v-t="'c.mainNav.navigation.signin'" />
          </router-link>
        </li>
      </ul>
    </template>
  </nav>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Avatar from '@/components/ui/Avatar'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import FormMixin from '@/mixins/forms/Form'
import fullName from '@/filters/fullName'
import isBlank from 'is-blank'

import {
  CLIENT_DEFAULT_LOGO
} from '@/store/modules/clients/types'

export default {
  name: 'MainNav',
  mixins: [FormMixin],
  filters: {
    fullName
  },
  components: {
    Avatar,
    FontAwesomeIcon
  },
  data () {
    return {
      clientDefaultLogo: CLIENT_DEFAULT_LOGO
    }
  },
  computed: {
    ...mapState({
      client: state => state.client,
      currentUser: state => state.currentUser.currentUser,
      streamNewEntries: state => state.app.stream.newEntries
    }),
    ...mapGetters([
      'isSignedIn'
    ]),
    clientLogo () {
      return isBlank(this.client.logo) ? this.clientDefaultLogo : this.client.logo.url
    },
    userDropdownOptions () {
      let options =  [
        { label: this.$t('c.mainNav.dropdown.user_settings'), value: 'settings', icon: 'user' },
        { label: this.$t('c.mainNav.dropdown.logout'), value: 'logout', icon: 'arrow-circle-right' }
      ]

      if(['owner', 'admin'].indexOf(this.currentUser.role) !== -1) {
        options.unshift({ label: this.$t('c.mainNav.dropdown.client_settings'), value: 'client', icon: 'cog' })
      }

      return options
    },
    isFoldersRoute () {
      const routeNames = ['ContentPage']
      return this.$route.name && routeNames.indexOf(this.$route.name) >= 0
    },
    isTeamsRoute () {
      const routeNames = ['TeamSettings', 'TeamMembers']
      return this.$route.name && routeNames.indexOf(this.$route.name) >= 0
    }
  },
  methods: {
    ...mapActions([
      'signOut'
    ]),
    handleUserDropdownSelect ({ value }) {
      switch (value) {
        case 'client':
          this.$router.push({ name: 'Client' })
          break
        case 'settings':
          this.$router.push({ name: 'ProfileSettings' })
          break
        case 'logout':
          this.signOut()
          break
      }
    }
  }
}
</script>

<style lang="sass" scoped>
@import '~styles/imports'

@keyframes pulse
  from
    transform: scale3d(1, 1, 1)
  50%
    transform: scale3d(1.2, 1.2, 1.2)
  to
    transform: scale3d(1, 1, 1)

.main-nav
  +clearfix()
  +z-index("navigation")
  background: $color-white
  border-bottom: 1px solid $color-grey-1
  height: rem-calc(82)
  left: 0
  padding: rem-calc(0 $grid-gutter-width-half)
  position: fixed
  right: 0
  top: 0
  display: flex

.main-nav__logo
    display: block
    max-width: rem-calc(120);
    max-height: rem-calc(40);
    height: rem-calc(40);

.main-nav__list
  +clearfix()
  display: flex
  height: 100%
  align-items: center

.main-nav__list--left
  margin-right: auto

  .main-nav__item
    padding-right: $grid-gutter-width-half

.main-nav__list--center
  margin: auto

  +respond-max($sm)
    position: fixed
    display: flex
    flex-direction: row
    justify-content: center
    bottom: 0
    left: 0
    z-index: 100
    width: 100vw
    height: 70px
    background-color: #fff
    border: 0
    border-top: 1px solid #eee

  .main-nav__link
    padding: 0 21px

    &:hover:after,
    &:focus:after,
    &.router-link-active:after
      position: absolute
      bottom: 0
      left: 0
      width: 100%
      content: ""
      border-bottom: 3px solid var(--mainNavLinkActive)

.main-nav__list--right
  margin-left: auto

  .main-nav__item
    padding-left: $grid-gutter-width-half

  .dropdown-input button
    outline: 0
    border: 1px solid transparent
    border-radius: 100%;

    &:active,
    &:focus
      border-color: black

.main-nav__item
  position: relative
  display: flex
  height: 100%
  align-items: center

  +respond-max($sm)
    flex: 1 1 auto!important
    justify-content: center

  & + &
    &--user
      .main-nav__link
        padding-right: rem-calc(40)
        position: relative



.main-nav__link
  +font-size(15, 15)

  display: flex
  height: 100%
  align-items: center

  color: var(--mainNavLink)
  font-weight: 700
  overflow: visible

  &:hover,
  &:focus
    color: var(--mainNavLinkHover)
    text-decoration: none
    outline: 0 none
  &.router-link-active
    color: var(--mainNavLinkActive)

  &.main-nav__logo
    color: var(--mainNavLogo)
    &:hover,
    &:focus
      color: var(--mainNavLogoHover)
    &.router-link-active
      color: var(--mainNavLogoActive)


  &--new
    position: relative

    &:before
      position: absolute
      right: 14px
      top: 26px
      display: block
      content: ''
      width: 8px
      height: 8px
      background: red
      border-radius: 4px

      animation-duration: 1s
      animation-fill-mode: both
      animation-name: pulse
      animation-iteration-count: infinite

  .fa
    +respond-min($sm)
      +font-size(24, 24)
    +respond-max($sm)
      +font-size(20, 20)

.button
  display: block

  .svg-inline--fa
    margin-right: 7px
    position: relative
    width: 20px

</style>



// WEBPACK FOOTER //
// src/components/layout/MainNav.vue